import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { useApi } from '../../hooks/useApi';

/**
 * Engine Health Badge — Document 2 · Phase 1
 *
 * Shows confirmed/rejected ratio of signal drafts for the current ISO week.
 * Color-coded: green ≥70%, amber 40-70%, red <40%.
 * Hidden until ≥5 confirmed signals this week.
 * Click → opens Filter Sensitivity settings.
 *
 * Live data from GET /api/signal-drafts/engine-health
 * Polls every 60s with single in-flight protection.
 * Read-only. Never writes. Untouched: Drafts Bell, Call Clarity, all other navbar logic.
 */

const POLL_INTERVAL_MS = 60_000;

const COLOR_TOKENS = {
  green: {
    dot: 'bg-emerald-500',
    ring: 'ring-emerald-500/20',
    text: 'text-emerald-700',
    hoverBg: 'hover:bg-emerald-50',
    label: 'Healthy',
  },
  amber: {
    dot: 'bg-amber-500',
    ring: 'ring-amber-500/20',
    text: 'text-amber-700',
    hoverBg: 'hover:bg-amber-50',
    label: 'Needs attention',
  },
  red: {
    dot: 'bg-red-500',
    ring: 'ring-red-500/20',
    text: 'text-red-700',
    hoverBg: 'hover:bg-red-50',
    label: 'Filter too tight',
  },
};

export function EngineHealthBadge() {
  const api = useApi();
  const [health, setHealth] = useState(null);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const fetchHealth = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        const data = await api.get('/signal-drafts/engine-health');
        if (mountedRef.current && data && typeof data === 'object') {
          setHealth(data);
        }
      } catch (err) {
        // Soft-fail: badge stays hidden if endpoint errors.
        // Never surface auth/network noise here — Drafts Bell handles those.
      } finally {
        inFlightRef.current = false;
      }
    };

    fetchHealth();
    const id = setInterval(fetchHealth, POLL_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!health || !health.show_badge) return null;

  const tokens = COLOR_TOKENS[health.color] || COLOR_TOKENS.amber;
  const pct = Math.round((health.ratio || 0) * 100);

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to="/intelligence-settings?tab=filter-sensitivity"
            data-testid="engine-health-badge"
            aria-label={`Engine Health: ${pct}% — ${health.confirmed_count} confirmed, ${health.rejected_count} rejected this week`}
            className={`group inline-flex items-center gap-1.5 h-9 px-2.5 rounded-full border border-stone-200 bg-white/70 backdrop-blur ${tokens.hoverBg} transition-colors`}
          >
            <span
              className={`relative inline-flex h-2 w-2 rounded-full ${tokens.dot} ring-4 ${tokens.ring}`}
              data-testid="engine-health-dot"
            >
              <span
                className={`absolute inset-0 rounded-full ${tokens.dot} opacity-60 animate-ping`}
                aria-hidden="true"
              />
            </span>
            <span
              className={`text-xs font-semibold tabular-nums ${tokens.text}`}
              data-testid="engine-health-pct"
            >
              {pct}%
            </span>
          </Link>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-1">
            <p className="font-semibold text-xs">
              Engine Health · {tokens.label}
            </p>
            <p className="text-xs text-muted-foreground">
              This week:{' '}
              <span className="font-medium text-emerald-700">
                {health.confirmed_count} confirmed
              </span>{' '}
              ·{' '}
              <span className="font-medium text-stone-600">
                {health.rejected_count} rejected
              </span>
            </p>
            <p className="text-[11px] text-muted-foreground pt-1 border-t mt-1">
              Click to tune filter sensitivity
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default EngineHealthBadge;
